import { Cross, ArrowLeft, Shield, MapPin, Smartphone, Users, Cookie, Database, Lock, Clock, Baby, FileText, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="bg-[#1C1917] text-white py-6 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#D4AF37] hover:text-[#F4D03F] transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar ao início</span>
          </Link>
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/3wtm88gi_logo-tema-claro-atualizado.png"
              alt="Santo Universo"
              className="w-8 h-8"
            />
            <span className="font-serif font-semibold">Santo Universo</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-6">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1917] mb-4">
            Política de Privacidade
          </h1>
          <p className="text-[#57534E]">
            Santo Universo - Aplicativo Católico
          </p>
          <p className="text-sm text-[#A8A29E] mt-2">
            Última atualização: 07 de Fevereiro de 2026
          </p>
        </div>

        {/* Policy Content */}
        <div className="prose prose-lg max-w-none">
          
          {/* Section 1 */}
          <section className="mb-10 p-6 bg-[#F5F2EB] rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">1</span>
              Introdução
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-4">
              O aplicativo Santo Universo ("nós", "nosso" ou "aplicativo") respeita a sua privacidade e está comprometido em proteger os dados pessoais que você compartilha conosco. Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos suas informações quando você utiliza nosso aplicativo.
            </p>
            <p className="text-[#57534E] leading-relaxed">
              Ao utilizar o Santo Universo, você concorda com as práticas descritas nesta política, em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)</strong> e demais legislações aplicáveis.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10 p-6 bg-white rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">2</span>
              Dados que Coletamos
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-6">
              Para oferecer uma experiência personalizada e funcional, podemos coletar os seguintes tipos de dados:
            </p>

            <div className="space-y-6">
              {/* 2.1 */}
              <div className="pl-4 border-l-4 border-[#D4AF37]">
                <h3 className="font-semibold text-[#1C1917] mb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  2.1. Dados de Localização
                </h3>
                <p className="text-[#57534E] mb-2">Com sua permissão, coletamos dados de localização geográfica para:</p>
                <ul className="list-disc list-inside text-[#57534E] space-y-1">
                  <li>Exibir informações litúrgicas relevantes para sua região</li>
                  <li>Ajustar o fuso horário para o Santo do Dia e liturgia diária</li>
                  <li>Personalizar conteúdos e anúncios com base em sua localização</li>
                </ul>
              </div>

              {/* 2.2 */}
              <div className="pl-4 border-l-4 border-[#D4AF37]">
                <h3 className="font-semibold text-[#1C1917] mb-2 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-[#D4AF37]" />
                  2.2. Dados de Uso do Aplicativo
                </h3>
                <ul className="list-disc list-inside text-[#57534E] space-y-1">
                  <li>Páginas e funcionalidades acessadas</li>
                  <li>Tempo de uso do aplicativo</li>
                  <li>Interações com conteúdos (orações, leituras, vídeos)</li>
                  <li>Preferências de tema (claro/escuro)</li>
                </ul>
              </div>

              {/* 2.3 */}
              <div className="pl-4 border-l-4 border-[#D4AF37]">
                <h3 className="font-semibold text-[#1C1917] mb-2 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-[#D4AF37]" />
                  2.3. Dados do Dispositivo
                </h3>
                <ul className="list-disc list-inside text-[#57534E] space-y-1">
                  <li>Modelo e sistema operacional do dispositivo</li>
                  <li>Identificadores únicos do dispositivo (para fins de publicidade)</li>
                  <li>Endereço IP</li>
                  <li>Informações de conexão de rede</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10 p-6 bg-[#F5F2EB] rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">3</span>
              Publicidade e Parceiros
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-4">
              O Santo Universo exibe anúncios para manter o aplicativo gratuito. Trabalhamos com os seguintes parceiros de publicidade:
            </p>
            
            <div className="bg-white p-4 rounded-xl mb-4">
              <h3 className="font-semibold text-[#1C1917] mb-3">Redes de Publicidade:</h3>
              <ul className="text-[#57534E] space-y-2">
                <li>• <strong>Google AdMob</strong> - Rede principal de anúncios</li>
                <li>• <strong>Amazon Ads</strong> - Anúncios de produtos Amazon</li>
                <li>• <strong>Mercado Livre Ads</strong> - Anúncios de produtos do Mercado Livre</li>
                <li>• <strong>Shopee Ads</strong> - Anúncios de produtos da Shopee</li>
                <li>• Outras redes parceiras de publicidade programática</li>
              </ul>
            </div>

            <p className="text-[#57534E] leading-relaxed mb-4">
              Esses parceiros podem coletar e usar dados como identificadores de publicidade, dados de localização e informações sobre seu comportamento de navegação para exibir anúncios personalizados. Cada parceiro possui sua própria política de privacidade:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">• Google AdMob: policies.google.com/privacy</a>
              <a href="https://amazon.com.br/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">• Amazon: amazon.com.br/privacy</a>
              <a href="https://mercadolivre.com.br/privacidade" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">• Mercado Livre: mercadolivre.com.br/privacidade</a>
              <a href="https://shopee.com.br/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">• Shopee: shopee.com.br/privacy</a>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10 p-6 bg-white rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">4</span>
              <Cookie className="w-6 h-6 text-[#D4AF37]" />
              Cookies e Tecnologias de Rastreamento
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-4">
              Utilizamos cookies e tecnologias similares para:
            </p>
            <ul className="list-disc list-inside text-[#57534E] space-y-2 mb-4">
              <li>Lembrar suas preferências (tema, última leitura)</li>
              <li>Analisar o uso do aplicativo e melhorar nossos serviços</li>
              <li>Exibir anúncios relevantes</li>
              <li>Medir a eficácia das campanhas publicitárias</li>
            </ul>
            <p className="text-[#57534E] italic">
              Você pode gerenciar suas preferências de cookies nas configurações do seu dispositivo.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10 p-6 bg-[#F5F2EB] rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">5</span>
              <Database className="w-6 h-6 text-[#D4AF37]" />
              Como Usamos Seus Dados
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-4">
              Utilizamos as informações coletadas para:
            </p>
            <ul className="list-disc list-inside text-[#57534E] space-y-2">
              <li>Fornecer e melhorar os serviços do aplicativo</li>
              <li>Personalizar sua experiência de uso</li>
              <li>Exibir conteúdo litúrgico adequado ao seu fuso horário</li>
              <li>Mostrar anúncios relevantes e personalizados</li>
              <li>Analisar tendências de uso para aprimorar funcionalidades</li>
              <li>Comunicar atualizações importantes sobre o aplicativo</li>
              <li>Cumprir obrigações legais</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-10 p-6 bg-white rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">6</span>
              <Users className="w-6 h-6 text-[#D4AF37]" />
              Compartilhamento de Dados
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-4">
              Podemos compartilhar seus dados com:
            </p>
            <ul className="list-disc list-inside text-[#57534E] space-y-2 mb-4">
              <li><strong>Parceiros de publicidade:</strong> Para exibição de anúncios personalizados</li>
              <li><strong>Provedores de serviços:</strong> Empresas que nos auxiliam na operação do aplicativo</li>
              <li><strong>Autoridades legais:</strong> Quando exigido por lei ou ordem judicial</li>
            </ul>
            <p className="text-[#800020] font-semibold">
              Não vendemos seus dados pessoais a terceiros.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10 p-6 bg-[#800020]/5 rounded-2xl border border-[#800020]/20">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#800020] text-white flex items-center justify-center text-sm font-bold">7</span>
              Seus Direitos (LGPD)
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-4">
              De acordo com a Lei Geral de Proteção de Dados, você tem direito a:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Confirmação:</strong>
                <span className="text-[#57534E]"> Saber se tratamos seus dados pessoais</span>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Acesso:</strong>
                <span className="text-[#57534E]"> Solicitar uma cópia dos seus dados</span>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Correção:</strong>
                <span className="text-[#57534E]"> Corrigir dados incompletos ou desatualizados</span>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Anonimização:</strong>
                <span className="text-[#57534E]"> Solicitar anonimização de dados desnecessários</span>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Eliminação:</strong>
                <span className="text-[#57534E]"> Pedir a exclusão de dados tratados com consentimento</span>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Portabilidade:</strong>
                <span className="text-[#57534E]"> Transferir seus dados para outro serviço</span>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Revogação:</strong>
                <span className="text-[#57534E]"> Retirar o consentimento a qualquer momento</span>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <strong className="text-[#800020]">Oposição:</strong>
                <span className="text-[#57534E]"> Opor-se ao tratamento em casos específicos</span>
              </div>
            </div>
            <p className="text-[#57534E] mt-4 italic">
              Para exercer qualquer destes direitos, entre em contato conosco através do e-mail indicado abaixo.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10 p-6 bg-white rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">8</span>
              <Lock className="w-6 h-6 text-[#D4AF37]" />
              Segurança dos Dados
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-4">
              Implementamos medidas técnicas e organizacionais para proteger seus dados pessoais contra acesso não autorizado, alteração, divulgação ou destruição. Isso inclui:
            </p>
            <ul className="list-disc list-inside text-[#57534E] space-y-2">
              <li>Criptografia de dados em trânsito (HTTPS/TLS)</li>
              <li>Controle de acesso restrito aos dados</li>
              <li>Monitoramento contínuo de segurança</li>
              <li>Headers de segurança avançados</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-10 p-6 bg-[#F5F2EB] rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">9</span>
              <Clock className="w-6 h-6 text-[#D4AF37]" />
              Retenção de Dados
            </h2>
            <p className="text-[#57534E] leading-relaxed">
              Mantemos seus dados pessoais apenas pelo tempo necessário para cumprir as finalidades para as quais foram coletados, incluindo obrigações legais, contábeis ou de relatórios. Dados de uso anônimos podem ser retidos por períodos mais longos para fins estatísticos.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10 p-6 bg-white rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">10</span>
              <Baby className="w-6 h-6 text-[#D4AF37]" />
              Menores de Idade
            </h2>
            <p className="text-[#57534E] leading-relaxed">
              O Santo Universo é um aplicativo de conteúdo religioso adequado para todas as idades. No entanto, não coletamos intencionalmente dados pessoais de crianças menores de 13 anos sem consentimento parental verificável. Se você é pai ou responsável e acredita que seu filho nos forneceu dados pessoais, entre em contato conosco.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10 p-6 bg-[#F5F2EB] rounded-2xl border border-[#D4AF37]/10">
            <h2 className="font-serif text-2xl font-bold text-[#1C1917] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-sm font-bold">11</span>
              <FileText className="w-6 h-6 text-[#D4AF37]" />
              Alterações nesta Política
            </h2>
            <p className="text-[#57534E] leading-relaxed">
              Podemos atualizar esta Política de Privacidade periodicamente. Quando fizermos alterações significativas, notificaremos você através do aplicativo ou por outros meios apropriados. Recomendamos que você revise esta política regularmente.
            </p>
          </section>

          {/* Section 12 - Contact */}
          <section className="mb-10 p-6 bg-[#1C1917] rounded-2xl text-white">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#1C1917] flex items-center justify-center text-sm font-bold">12</span>
              <Mail className="w-6 h-6 text-[#D4AF37]" />
              Contato
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Privacidade ou ao tratamento dos seus dados pessoais, entre em contato conosco:
            </p>
            <div className="bg-white/10 p-4 rounded-xl">
              <p className="font-serif text-xl font-semibold text-[#D4AF37] mb-2">Santo Universo</p>
              <p className="text-white/90">
                <strong>E-mail:</strong> <a href="mailto:contato@universosaintapp.com.br" className="text-[#D4AF37] hover:underline">contato@universosaintapp.com.br</a>
              </p>
              <p className="text-white/90">
                <strong>Website:</strong> <a href="https://universosaintapp.com.br" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">universosaintapp.com.br</a>
              </p>
            </div>
          </section>

          {/* Final Note */}
          <div className="text-center p-6 bg-[#D4AF37]/10 rounded-2xl border border-[#D4AF37]/20">
            <p className="text-[#57534E] italic mb-4">
              Ao utilizar o aplicativo Santo Universo, você declara ter lido, compreendido e concordado com esta Política de Privacidade.
            </p>
            <div className="flex items-center justify-center gap-2 text-[#D4AF37]">
              <Cross className="w-5 h-5" />
              <span className="font-serif font-semibold">© 2026 Santo Universo. Todos os direitos reservados.</span>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1C1917] text-white py-6 px-6 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/" className="text-[#D4AF37] hover:underline">
            ← Voltar para a página inicial
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
